import React, { useCallback, useState } from "react";

import Button from "veneer/Button";
import Stack from "veneer/Stack";
import TextInput from "veneer/TextInput";

import { URL_PATTERN } from "cortex/ui/src/constant/app";
import { webPageToMediaEntry } from "cortex/ui/src/fleet/view/media-library/utils";
import Notification from "cortex/ui/src/ui/notification";

interface WebPageFormState {
  duration: number | string;
  name: string;
  url: string;
}

interface WebPageFormErrors {
  duration?: string;
  name?: string;
  url?: string;
}

interface AddWebPageFormProps {
  addSpots?: (payload: { priority: string; spots: unknown[] }) => void;
  priority: string;
}

function getFieldError(
  field: keyof WebPageFormState,
  webPage: WebPageFormState,
): string | undefined {
  const { duration, name, url } = webPage;
  const trimmedName = name.trim();
  const trimmedUrl = url.trim();
  const numericDuration = Number(duration);

  switch (field) {
    case "name":
      if (!trimmedName) {
        return "Name is required.";
      }
      return undefined;
    case "url":
      if (!trimmedUrl) {
        return "URL is required.";
      }

      if (!URL_PATTERN.test(trimmedUrl)) {
        return "Please provide a valid URL.";
      }

      return undefined;
    case "duration":
      if (String(duration).trim() === "") {
        return "Duration is required.";
      }

      if (Number.isNaN(numericDuration) || numericDuration <= 0) {
        return "Duration must be greater than 0 seconds.";
      }

      return undefined;
    default:
      return undefined;
  }
}

function getFormErrors(webPage: WebPageFormState): WebPageFormErrors {
  return {
    duration: getFieldError("duration", webPage),
    name: getFieldError("name", webPage),
    url: getFieldError("url", webPage),
  };
}

export default function AddWebPageForm({
  addSpots = () => {},
  priority,
}: AddWebPageFormProps) {
  const [fieldErrors, setFieldErrors] = useState<WebPageFormErrors>({});
  const [successMessage, setSuccessMessage] = useState("");
  const [webPage, setWebPage] = useState<WebPageFormState>({
    duration: 15,
    name: "",
    url: "",
  });

  const validateField = useCallback(
    (field: keyof WebPageFormState, nextWebPage: WebPageFormState) => {
      setFieldErrors((currentErrors) => ({
        ...currentErrors,
        [field]: getFieldError(field, nextWebPage),
      }));
    },
    [],
  );

  const handleChange = useCallback(
    (field: keyof WebPageFormState) => (value: string | null) => {
      setWebPage((currentWebPage) => {
        const nextWebPage = {
          ...currentWebPage,
          [field]: value ?? "",
        };
        if (fieldErrors[field]) {
          validateField(field, nextWebPage);
        }

        return nextWebPage;
      });
      setSuccessMessage("");
    },
    [fieldErrors, validateField],
  );

  const handleAddWebPage = useCallback(() => {
    setFieldErrors({});
    const errors = getFormErrors(webPage);
    const hasErrors = Object.values(errors).some(Boolean);

    if (hasErrors) {
      setFieldErrors(errors);
      setSuccessMessage("");
      return;
    }
    addSpots({
      priority,
      spots: [webPageToMediaEntry(webPage)],
    });
    setSuccessMessage("Success! Web page added to strategy.");
  }, [addSpots, priority, webPage]);

  const { duration, name, url } = webPage;

  return (
    <Stack dataTestId="add-web-page-form" spacing={2}>
      <Notification success={successMessage} />
      <TextInput
        dataTestId="add-web-page-form-name"
        error={Boolean(fieldErrors.name)}
        fullWidth
        helperText={fieldErrors.name}
        label="Name"
        onChange={handleChange("name")}
        placeholder="My Great Website"
        required
        value={name}
      />
      <Stack direction="row" spacing={2}>
        <Stack width="75%">
          <TextInput
            dataTestId="add-web-page-form-url"
            error={Boolean(fieldErrors.url)}
            fullWidth
            helperText={fieldErrors.url}
            label="URL"
            onChange={handleChange("url")}
            placeholder="https://www.vistarmedia.com"
            required
            value={url}
          />
        </Stack>
        <Stack width="25%">
          <TextInput
            dataTestId="add-web-page-form-duration"
            error={Boolean(fieldErrors.duration)}
            fullWidth
            helperText={fieldErrors.duration}
            label="Duration (in seconds)"
            onChange={handleChange("duration")}
            placeholder="Duration (seconds)"
            required
            value={String(duration)}
          />
        </Stack>
      </Stack>
      <Button
        dataTestId="add-web-page-form-submit"
        icon="add"
        onClick={handleAddWebPage}
        title="Add this web page to your strategy"
        variant="outlined"
      >
        Add to Strategy
      </Button>
    </Stack>
  );
}
